// calendar.ts
import Global from './global';
import { SheetHelper } from './sheetHelper';
import { easepick, DateTime, RangePlugin, LockPlugin } from '@easepick/bundle';
import { Modal } from 'bootstrap';
import moment from 'moment';

type PriceWithLos = {
    price: string;
    minLengthOfStay: string;
};

const prices: Record<string, string> = {}; // Fill with actual date price map like { '2025-08-08': '100', ... }
const pricesWithLos: Record<string, PriceWithLos> = {}; // Fill with actual date price map like { '2025-08-08': '100', ... }
const monthsRequested: string[] = [];
const maxNights = 30;

function _getEarliestBookableDate(): Date {
    const todayDate = new Date();
    const systemHour = parseInt(
        (document.getElementById("SearchBarForm_systemHour") as HTMLInputElement).value,
        10
    );

    let bookableDate: Date;

    // Call Centre can book for today - everyone else it's tomorrow onwards
    if (document.getElementById("callCentreModeContainer") !== null) {
        bookableDate = todayDate;
    } else {
        bookableDate = new Date(
            todayDate.getFullYear(),
            todayDate.getMonth(),
            todayDate.getDate() + 1
        );
    }

    const searchProviderType = (document.getElementById("SearchBarForm_SearchProviderType") as HTMLInputElement).value;

    // Earliest bookable date is +3 days for Holiday Homes
    if (searchProviderType === "HolidayHome") {
        bookableDate = new Date(
            bookableDate.getFullYear(),
            bookableDate.getMonth(),
            bookableDate.getDate() + 3
        );
    }

    // Allow to book for today only if the hour is < 16:00, otherwise it's tomorrow
    if (systemHour >= 16) {
        bookableDate = new Date(
            bookableDate.getFullYear(),
            bookableDate.getMonth(),
            bookableDate.getDate() + 1
        );
    }

    // Check for goldenireland tenant and adjust date if it falls on Friday or Saturday
    if (typeof (window as any).Global?.getTenant === "function" && (window as any).Global.getTenant() === "goldenireland") {
        const dayOfWeek = bookableDate.getDay(); // 5 = Friday, 6 = Saturday
        if (dayOfWeek === 5) { // Friday
            bookableDate = new Date(
                bookableDate.getFullYear(),
                bookableDate.getMonth(),
                bookableDate.getDate() + 2
            );
        } else if (dayOfWeek === 6) { // Saturday
            bookableDate = new Date(
                bookableDate.getFullYear(),
                bookableDate.getMonth(),
                bookableDate.getDate() + 1
            );
        }
    }

    return bookableDate;
}

const toYyyyMmDd = (startDate: string | Date): string => {
    const date =
        startDate instanceof Date
            ? startDate
            : new Date(startDate);

    return date.toISOString().split('T')[0];
};

function _checkMinMaxNights(startDate: string | Date, endDate: string | Date, picker: any): boolean {
    if (!startDate || !endDate) return true;

    const priceObj = pricesWithLos[toYyyyMmDd(startDate)];

    const nights = moment(endDate).diff(moment(startDate), "days");

    let minNights = 0;

    if (priceObj) {
        minNights = Number(priceObj.minLengthOfStay);
    }

    const maxNightsField = document.querySelector('#MaxNights') as HTMLInputElement | null;
    let maxNights = 0;

    if (maxNightsField) {
        maxNights = Number(maxNightsField.value);
    }

    if (nights < minNights) {
        const modal = new Modal(document.getElementById('minNightsModal')!);

        const minNightsDisplayField = document.querySelector('#minNightsModalDisplayField') as HTMLSpanElement | null;
        if (minNightsDisplayField) {
            minNightsDisplayField.innerHTML = minNights.toString();
        }

        modal.show();

        if (picker) {
            picker.clear();
            picker.renderAll();
        }

        return false; // failed
    }

    if (nights > maxNights) {
        const modal = new Modal(document.getElementById('maxNightsModal')!);
        modal.show();

        if (picker) {
            picker.clear();
            picker.renderAll();
        }

        return false; // failed
    }

    return true; // passed
}

function _isRangeContainsCheckOutOnly(
    startDate: string | Date,
    endDate: string | Date,
    picker: any,
    pickerId: string
): boolean {
    if (!startDate || !endDate) return false;

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    // Find the specific container by ID
    const containerEl = document.getElementById(pickerId);
    if (!containerEl) return false;

    // The actual easepick wrapper is inside the container
    const pickerEl = containerEl.querySelector('.easepick-wrapper') as HTMLElement | null;
    if (!pickerEl || !pickerEl.shadowRoot) return false;

    // Get all day cells in this calendar
    const dayCells = pickerEl.shadowRoot.querySelectorAll('.day.unit');

    // Iterate over each date in the range, excluding the last date
    let current = startMoment.clone();
    while (current.isBefore(endMoment)) {
        const currentStr = current.format('YYYY-MM-DD');
        for (const cell of dayCells) {
            const cellEl = cell as HTMLElement;
            const cellTime = cellEl.getAttribute('data-time');
            if (!cellTime) continue;

            const cellDate = moment(parseInt(cellTime, 10)).format('YYYY-MM-DD');

            if (cellDate === currentStr && cellEl.classList.contains('check-out-only')) {
                // Show modal and reset picker
                const modal = new Modal(document.getElementById('checkOutOnlyModal')!);
                modal.show();

                picker.clear();
                picker.renderAll();

                return true;
            }
        }

        current.add(1, 'day');
    }

    return false;
}

function _setEasePickerDate(picker: any) {
    const startDate = picker.getStartDate(); // returns a DateTime or undefined
    const endDate = picker.getEndDate();

    const startDateInput = picker.getStartDate();

    moment.locale(window.culture)

    if (startDateInput !== undefined) {
        const selectedStartDate = moment(startDateInput);
        (document.getElementById('SearchBarForm_ArrivalDate') as HTMLInputElement).value = selectedStartDate.format('DD-MM-YYYY');

        let selectedEndDate: moment.Moment | undefined;
        const endDateInput = picker.getEndDate();
        if (endDateInput !== '') {
            selectedEndDate = moment(endDateInput, 'DD-MM-YYYY');
        }

        if (selectedEndDate && selectedEndDate.diff(selectedStartDate, 'days') === 0) {
            selectedEndDate = selectedEndDate.add(1, 'days');
            picker.setStartDate(selectedStartDate.format('DD-MM-YYYY'));
            picker.setEndDate(selectedEndDate.format('DD-MM-YYYY'));
        }

        if (selectedEndDate !== undefined) {
            const numberOfNights = selectedEndDate.diff(selectedStartDate, 'days');
            (document.getElementById('SearchBarForm_NumberOfNights') as HTMLInputElement).value = numberOfNights.toString();

            const counterStartDate = new Date('1990-01-01');
            const diffDays = Global.dateDiffInDays(counterStartDate, selectedStartDate.toDate());
            (document.getElementById('SearchBarForm_ArrivalDateCounter') as HTMLInputElement).value = diffDays.toString();
            (document.getElementById('SearchBarForm_DepartureDateCounter') as HTMLInputElement).value = (diffDays + numberOfNights).toString();

            (document.getElementById('SearchBarForm_BreakArrivalDateCounter') as HTMLInputElement).value = diffDays.toString();
            (document.getElementById('SearchBarForm_BreakDepartureDateCounter') as HTMLInputElement).value = (diffDays + numberOfNights).toString();

            let nightsText = window._screenMessages.Nights;
            if (parseInt(numberOfNights.toString()) === 1) nightsText = window._screenMessages.Night;

            const calendarEl = document.getElementById('calendarDisplay');
            if (calendarEl) {
                calendarEl.innerHTML = `${selectedStartDate.format('ddd D MMM')} (${numberOfNights} ${nightsText})`;
            }

            const hotelCalendarEl = document.getElementById('hotelCalendarDisplay');
            if (hotelCalendarEl) {
                hotelCalendarEl.innerHTML = `${selectedStartDate.format('ddd D MMM')} (${numberOfNights} ${nightsText})`;
            }
        }
    }
}

function _loadCalendarPrices(nd: moment.Moment, monthRequested: string, picker: any) {
    const sdd = Global.dateDiffInDays(new Date('1990-01-01'), nd.toDate());
    const dim = Global.getDaysInMonth(nd.year(), nd.month() + 1);
    const edd = sdd + dim - 1;

    let nn = 1;
    const minNights = parseInt($("#MinNights").val() as string, 10);
    if (nn !== minNights) {
        nn = minNights;
    }
    const ri = $('#SearchBarForm_RoomInfoString').val();
    const h = $('#Hotel_ID').val();
    const rc = $('#SearchBarForm_RateCodeSlug').val();

    const flexiUrl = `/Hotel/GetCalendarAvailability?hotelId=${h}&startDateCounter=${sdd}&endDateCounter=${edd}&numberOfNights=${nn}&roomInfoString=${ri}&rateCodeSlug=${rc}`;

    $.ajax({
        type: 'GET',
        url: flexiUrl,
        success: (data: any[]) => {
            for (let j = 0; j < data.length; j++) {
                //_addCalendarPrice(data[j].item1, data[j].item2);
                _addCalendarPriceWithMinLos(data[j].date, data[j].price, data[j].minLengthOfStay);
            }

            if (!monthsRequested.includes(monthRequested)) {
                monthsRequested.push(monthRequested);
            }

            picker.renderAll()
        },
        error: () => { },
    });
}

function _loadCalendarPricesForRange(visibleMonths: moment.Moment[], picker: any) {
    if (!visibleMonths.length) return;

    // Get first day of the earliest month
    const firstMonth = moment.min(visibleMonths);
    const firstDate = firstMonth.clone().startOf('month');

    // Get last day of the latest month
    const lastMonth = moment.max(visibleMonths);
    const lastDate = lastMonth.clone().endOf('month');

    // Convert dates to your 'date counter' format
    const sdd = Global.dateDiffInDays(new Date('1990-01-01'), firstDate.toDate());
    const edd = Global.dateDiffInDays(new Date('1990-01-01'), lastDate.toDate());

    let nn = 1;
    const minNights = parseInt($("#MinNights").val() as string, 10);
    if (nn !== minNights) {
        nn = minNights;
    }
    const ri = $('#SearchBarForm_RoomInfoString').val();
    const h = $('#Hotel_ID').val();
    const rc = $('#SearchBarForm_RateCodeSlug').val();

    const flexiUrl = `/Hotel/GetCalendarAvailability?hotelId=${h}&startDateCounter=${sdd}&endDateCounter=${edd}&numberOfNights=${nn}&roomInfoString=${ri}&rateCodeSlug=${rc}`;

    $.ajax({
        type: 'GET',
        url: flexiUrl,
        success: (data: any[]) => {
            for (let j = 0; j < data.length; j++) {
                _addCalendarPriceWithMinLos(data[j].date, data[j].price, data[j].minLengthOfStay);
            }

            // Mark all months as requested
            for (const nd of visibleMonths) {
                const monthRequested = `${nd.year()}-${nd.month() + 1}`;
                if (!monthsRequested.includes(monthRequested)) {
                    monthsRequested.push(monthRequested);
                }
            }

            picker.renderAll();
        },
        error: () => { },
    });
}

const _addCalendarPrice = (calDate: string, calPrice: string | number): void => {
    prices[calDate] = String(calPrice);
};

const _addCalendarPriceWithMinLos = (calDate: string, calPrice: string | number, calMinLos: string | number): void => {
    pricesWithLos[calDate] = {
        price: String(calPrice),
        minLengthOfStay: String(calMinLos)
    };
};

export function initCalendar(inlineCalendar: boolean) {
    const calendarWidgetContainer = document.getElementById('calendar-widget-container');
    if (!calendarWidgetContainer) return;

    // Prepare initial dates
    let selectedStartDate: moment.Moment | undefined;
    let selectedEndDate: moment.Moment | undefined;

    const arrivalDateInput = (document.getElementById('SearchBarForm_ArrivalDate') as HTMLInputElement | null)?.value;
    const numberOfNightsInput = (document.getElementById('SearchBarForm_NumberOfNights') as HTMLInputElement | null)?.value;

    if (
        arrivalDateInput &&
        arrivalDateInput !== '' &&
        numberOfNightsInput &&
        numberOfNightsInput !== '' &&
        !isNaN(Number(numberOfNightsInput))
    ) {
        selectedStartDate = moment(arrivalDateInput, 'DD-MM-YYYY');
        selectedEndDate = moment(arrivalDateInput, 'DD-MM-YYYY').add(Number(numberOfNightsInput), 'days');
    }

    const startDate = selectedStartDate ? new DateTime(selectedStartDate.toDate()) : undefined;
    const endDate = selectedEndDate ? new DateTime(selectedEndDate.toDate()) : undefined;
    const tenant = Global.getCurrentTenant();

    const picker = new (easepick.create)({
        element: calendarWidgetContainer!,
        css: [
            'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css',
            `${window.location.origin}/css/${tenant}/calendar.css`
        ],
        zIndex: 1000,
        grid: 2,
        format: 'DD-MM-YYYY',
        lang: window.culture,
        inline: true,
        calendars: 2,
        plugins: [RangePlugin, LockPlugin],
        RangePlugin: {
            repick: true,
            startDate: startDate,
            endDate: endDate,
            tooltipNumber: (num: number) => num - 1,
            locale: {
                one: window._screenMessages.Night,
                other: window._screenMessages.Nights,
            },
        },
        LockPlugin: {
            inseparable: true,
            minDate: _getEarliestBookableDate(),
            minDays: 1,
            maxDays: 7
        },
        setup: (picker: any) => {
            picker.on('select', (e: any) => {
                const startDate = picker.getStartDate();
                const endDate = picker.getEndDate();

                // Only proceed if both start and end dates are selected
                if (startDate && endDate) {
                    _setEasePickerDate(picker);

                    // Close the sheet (imported from SearchBarFields)
                    SheetHelper.closeAllSheets();
                }
            });

            picker.on('hide', () => {
                //_setEasePickerDate(picker);
                document.querySelectorAll('.tooltip').forEach((el) => (el as HTMLElement).style.display = 'none');
            });

            picker.on('show', () => {
                // Custom validation can go here
            });

            picker.on('render', () => {
                // Get the first visible day
                const pickerEl = document.querySelector('.easepick-wrapper');
                if (!pickerEl) return;

                // Access its shadow root
                const shadowRoot = pickerEl.shadowRoot;
                if (!shadowRoot) return;

                const prevBtn = shadowRoot.querySelector('.previous-button');
                const nextBtn = shadowRoot.querySelector('.next-button');

                if (prevBtn) prevBtn.setAttribute('aria-label', 'Previous month');
                if (nextBtn) nextBtn.setAttribute('aria-label', 'Next month');
            });
        },
    });

    // Set default dates if passed through model
    if (selectedStartDate && selectedEndDate) {
        // 1. Select the range
        picker.setStartDate(selectedStartDate.format('DD-MM-YYYY'));
        picker.setEndDate(selectedEndDate.format('DD-MM-YYYY'));

        // 2. Scroll calendar to start month
        const firstDayOfMonth = new Date(
            selectedStartDate.year(),
            selectedStartDate.month(),
            1
        );
        picker.gotoDate(firstDayOfMonth);

        // 3. Update inputs and headers
        _setEasePickerDate(picker);
    }

    // ---------------------------
    // Responsive calendar control
    // ---------------------------
    const breakpoint = 768; // Adjust to your "phone-lg" width
    let resizeTimeout: number | undefined;

    function updateCalendarLayout() {
        const newCount = window.innerWidth < breakpoint ? 1 : 2;
        if (picker.options.calendars !== newCount) {
            picker.options.calendars = newCount;
            picker.renderAll();
        }
    }

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(updateCalendarLayout, 150);
    });

    updateCalendarLayout(); // run once on init
}

export function initHotelCalendar(inlineCalendar: boolean) {
    const calendarWidgetContainer = document.getElementById('hotel-calendar-widget-container');
    if (!calendarWidgetContainer) return;

    // Prepare initial dates
    let selectedStartDate: moment.Moment | undefined;
    let selectedEndDate: moment.Moment | undefined;

    const arrivalDateInput = (document.getElementById('SearchBarForm_ArrivalDate') as HTMLInputElement | null)?.value;
    const numberOfNightsInput = (document.getElementById('SearchBarForm_NumberOfNights') as HTMLInputElement | null)?.value;
    if (
        arrivalDateInput &&
        arrivalDateInput !== '' &&
        numberOfNightsInput &&
        numberOfNightsInput !== '' &&
        !isNaN(Number(numberOfNightsInput))
    ) {
        selectedStartDate = moment(arrivalDateInput, 'DD-MM-YYYY');
        selectedEndDate = moment(arrivalDateInput, 'DD-MM-YYYY').add(Number(numberOfNightsInput), 'days');
    }

    const startDate = selectedStartDate ? new DateTime(selectedStartDate.toDate()) : undefined;
    const endDate = selectedEndDate ? new DateTime(selectedEndDate.toDate()) : undefined;
    const tenant = Global.getCurrentTenant();

    const picker = new (easepick.create)({
        element: calendarWidgetContainer!,
        css: [
            'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css',
            `${window.location.origin}/css/${tenant}/calendar.css`
        ],
        zIndex: 1000,
        grid: 2,
        format: 'DD-MM-YYYY',
        lang: window.culture,
        inline: true,
        calendars: 2,
        plugins: [RangePlugin, LockPlugin],
        RangePlugin: {
            repick: true,
            startDate: startDate,
            endDate: endDate,
            tooltipNumber: (num: number) => num - 1,
            locale: {
                one: window._screenMessages.Night,
                other: window._screenMessages.Nights,
            },
        },
        LockPlugin: {
            inseparable: true,
            minDate: _getEarliestBookableDate(),
            minDays: 1,
            maxDays: 7
        },
        setup: (picker: any) => {
            // Get the first visible day
            // Get the Easepick wrapper that’s the *next sibling*
            const pickerEl = calendarWidgetContainer.nextElementSibling?.classList.contains('easepick-wrapper')
                ? calendarWidgetContainer.nextElementSibling as HTMLElement
                : null;

            if (!pickerEl) return;

            // Access its shadow root
            const shadowRoot = pickerEl.shadowRoot;
            if (!shadowRoot) return;

            const firstDayEl = shadowRoot.querySelector('.day.unit');
            if (!firstDayEl) return;


            const firstTimestamp = parseInt(firstDayEl.getAttribute('data-time')!, 10);
            let firstDate = moment(firstTimestamp);

            if (selectedStartDate) firstDate = selectedStartDate;

            // Get all visible months in the calendar (2 calendars side by side)
            const visibleMonths: moment.Moment[] = [];

            // Assuming 2 months are visible and get the next one for good measure
            for (let i = 0; i <= 2; i++) {
                const nd = firstDate.clone().startOf('month').add(i, 'month');
                visibleMonths.push(nd);
            }

            // Now loop through them and request prices
            //for (const nd of visibleMonths) {
            //    const monthRequested = `${nd.year()}-${nd.month() + 1}`;
            //    if (!monthsRequested.includes(monthRequested)) {
            //        _loadCalendarPrices(nd, monthRequested, picker);
            //    }
            //}
            _loadCalendarPricesForRange(visibleMonths, picker);

            picker.on('click', (e: MouseEvent) => {
                const target = e.target as HTMLElement | null;
                if (!target) return;

                const element = target.closest('.unit');
                const monthAdd = target.classList.contains('next-button');
                const monthRemove = target.classList.contains('previous-button');
                const closeOutOnlyDate = target.classList.contains('check-out-only');

                const d = picker.calendars[0] ?? null;
                if (d && (monthAdd || monthRemove)) {
                    for (let i = 1; i <= picker.options.calendars; i++) {
                        const nd = moment(new Date(d));
                        if (monthAdd) nd.add(i, 'month');
                        else if (monthRemove) nd.add(-i, 'month');

                        const monthRequested = `${nd.year()}-${nd.month() + 1}`;

                        if (!monthsRequested.includes(monthRequested)) {
                            _loadCalendarPrices(nd, monthRequested, picker);
                        }
                    }
                }
            });

            picker.on('select', (e: any) => {
                const startDate = picker.getStartDate();
                const endDate = picker.getEndDate();

                // Only proceed if both start and end dates are selected
                if (startDate && endDate) {
                    if (_checkMinMaxNights(startDate, endDate, picker) && !_isRangeContainsCheckOutOnly(startDate, endDate, picker, "hotelCalendarSheetContent")) {
                        _setEasePickerDate(picker);

                        SheetHelper.closeAllSheets();
                    }
                }
            });

            picker.on('render', () => {
                // Get the first visible day
                const pickerEl = document.querySelector('.easepick-wrapper');
                if (!pickerEl) return;

                // Access its shadow root
                const shadowRoot = pickerEl.shadowRoot;
                if (!shadowRoot) return;

                const prevBtn = shadowRoot.querySelector('.previous-button');
                const nextBtn = shadowRoot.querySelector('.next-button');

                if (prevBtn) prevBtn.setAttribute('aria-label', 'Previous month');
                if (nextBtn) nextBtn.setAttribute('aria-label', 'Next month');
            });

            picker.on('hide', () => {
                document.querySelectorAll('.tooltip').forEach((el) => (el as HTMLElement).style.display = 'none');
            });

            picker.on('show', () => {
                // Custom validation can go here
            });

            picker.on('view', (e: any) => {
                const h = $('#Hotel_ID').val();
                if (h !== undefined) {
                    const d = e.detail.date ? e.detail.date.format('YYYY-MM-DD') : null;
                    if (!d) return;

                    if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay') {
                        const target = e.detail.target as HTMLElement;
                        if (!target) return;

                        // Remove any existing content inside the day cell
                        target.innerHTML = '';

                        // Create (or reuse) the span for price placeholder
                        const span =
                            (target.querySelector('.day-price') as HTMLSpanElement | null) ??
                            document.createElement('span');

                        span.className = 'day-price';

                        // Append the span and apply loading class
                        target.append(span);
                        target.classList.add('loading');
                    }

                    if (pricesWithLos[d] !== undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const span = e.detail.target.querySelector('.day-price') || document.createElement('span');
                        if (pricesWithLos[d].price == 'X') {
                            const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                            if (pricesWithLos[lastDate].price !== 'X') {
                                e.detail.target.classList.add('check-out-only');
                                //e.detail.target.setAttribute('title', 'Check out date only');
                                $(e.detail.target).tooltip({ placement: 'bottom' });
                            }
                            else
                            {
                                e.detail.target.classList.add('not-available');
                            }
                        }
                        else {
                            span.className = 'day-price';
                            span.innerHTML = pricesWithLos[d].price;
                            e.detail.target.append(span);
                        }
                        
                    } else if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                        if (pricesWithLos[lastDate] !== undefined) {
                            e.detail.target.classList.add('check-out-only');
                            //e.detail.target.setAttribute('title', 'Check out date only');
                            $(e.detail.target).tooltip({ placement: 'bottom' });
                        } else {
                            e.detail.target.classList.add('locked');
                        }
                    }
                }
            });
        },
    });

    // Set default dates if passed through model
    if (selectedStartDate && selectedEndDate) {
        // 1. Select the range
        picker.setStartDate(selectedStartDate.format('DD-MM-YYYY'));
        picker.setEndDate(selectedEndDate.format('DD-MM-YYYY'));

        // 2. Scroll calendar to start month
        const firstDayOfMonth = new Date(
            selectedStartDate.year(),
            selectedStartDate.month(),
            1
        );
        picker.gotoDate(firstDayOfMonth);

        // 3. Update inputs and headers
        // Only set the date if the user has selected dates
        const selectDatesEl = document.querySelector('.db-no-selected-dates');
        if (!selectDatesEl) _setEasePickerDate(picker);
    }

    const noDatesCalendarWidgetContainer = document.getElementById('no-dates-calendar-widget-container');
    if (calendarWidgetContainer) picker.clear();

    // ---------------------------
    // Responsive calendar control
    // ---------------------------
    const breakpoint = 768; // Adjust to your "phone-lg" width
    let resizeTimeout: number | undefined;

    function updateCalendarLayout() {
        const newCount = window.innerWidth < breakpoint ? 1 : 2;
        if (picker.options.calendars !== newCount) {
            picker.options.calendars = newCount;
            picker.renderAll();
        }
    }

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(updateCalendarLayout, 150);
    });

    updateCalendarLayout(); // run once on init
}

export function initNoDatesCalendar(inlineCalendar: boolean) {
    const calendarWidgetContainer = document.getElementById('no-dates-calendar-widget-container');
    if (!calendarWidgetContainer) return;

    // Prepare initial dates
    let selectedStartDate: moment.Moment | undefined;
    let selectedEndDate: moment.Moment | undefined;

    const arrivalDateInput = (document.getElementById('SearchBarForm_ArrivalDate') as HTMLInputElement | null)?.value;
    const numberOfNightsInput = (document.getElementById('SearchBarForm_NumberOfNights') as HTMLInputElement | null)?.value;

    if (
        arrivalDateInput &&
        arrivalDateInput !== '' &&
        numberOfNightsInput &&
        numberOfNightsInput !== '' &&
        !isNaN(Number(numberOfNightsInput))
    ) {
        selectedStartDate = moment(arrivalDateInput, 'DD-MM-YYYY');
        selectedEndDate = moment(arrivalDateInput, 'DD-MM-YYYY').add(Number(numberOfNightsInput), 'days');
    }

    const startDate = selectedStartDate ? new DateTime(selectedStartDate.toDate()) : undefined;
    const endDate = selectedEndDate ? new DateTime(selectedEndDate.toDate()) : undefined;
    const tenant = Global.getCurrentTenant();

    const picker = new (easepick.create)({
        element: calendarWidgetContainer!,
        css: [
            'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css',
            `${window.location.origin}/css/${tenant}/calendar.css`
        ],
        zIndex: 1000,
        grid: 2,
        format: 'DD-MM-YYYY',
        lang: window.culture,
        inline: true,
        calendars: 2,
        plugins: [RangePlugin, LockPlugin],
        RangePlugin: {
            repick: true,
            startDate: startDate,
            endDate: endDate,
            tooltipNumber: (num: number) => num - 1,
            locale: {
                one: window._screenMessages.Night,
                other: window._screenMessages.Nights,
            },
        },
        LockPlugin: {
            inseparable: true,
            minDate: _getEarliestBookableDate(),
            minDays: 2,
            maxDays: 7
        },
        setup: (picker: any) => {
            // Get the first visible day
            // Get the Easepick wrapper that’s the *next sibling*
            const pickerEl = calendarWidgetContainer.nextElementSibling?.classList.contains('easepick-wrapper')
                ? calendarWidgetContainer.nextElementSibling as HTMLElement
                : null;

            if (!pickerEl) return;

            // Access its shadow root
            const shadowRoot = pickerEl.shadowRoot;
            if (!shadowRoot) return;

            const firstDayEl = shadowRoot.querySelector('.day.unit');
            if (!firstDayEl) return;

            const firstTimestamp = parseInt(firstDayEl.getAttribute('data-time')!, 10);
            const firstDate = moment(firstTimestamp);

            // Get all visible months in the calendar (2 calendars side by side)
            const visibleMonths: moment.Moment[] = [];

            // Assuming 2 months are visible and get the next one for good measure
            for (let i = 0; i <= 2; i++) {
                const nd = firstDate.clone().startOf('month').add(i, 'month');
                visibleMonths.push(nd);
            }

            // Now loop through them and request prices
            //for (const nd of visibleMonths) {
            //    const monthRequested = `${nd.year()}-${nd.month() + 1}`;
            //    if (!monthsRequested.includes(monthRequested)) {
            //        _loadCalendarPrices(nd, monthRequested, picker);
            //    }
            //}
            _loadCalendarPricesForRange(visibleMonths, picker);
            
            picker.on('click', (e: MouseEvent) => {
                const target = e.target as HTMLElement | null;
                if (!target) return;

                const element = target.closest('.unit');
                const monthAdd = target.classList.contains('next-button');
                const monthRemove = target.classList.contains('previous-button');
                const closeOutOnlyDate = target.classList.contains('check-out-only');

                if (closeOutOnlyDate && picker.datePicked.length === 0) {
                    e.stopPropagation();
                    picker.datePicked = [];
                    picker.setStartDate(null);
                    return;
                }

                const d = picker.calendars[0] ?? null;
                if (d && (monthAdd || monthRemove)) {
                    for (let i = 1; i <= picker.options.calendars; i++) {
                        const nd = moment(new Date(d));
                        if (monthAdd) nd.add(i, 'month');
                        else if (monthRemove) nd.add(-i, 'month');

                        const monthRequested = `${nd.year()}-${nd.month() + 1}`;

                        if (!monthsRequested.includes(monthRequested)) {
                            const sdd = Global.dateDiffInDays(new Date('1990-01-01'), nd.toDate());
                            const dim = Global.getDaysInMonth(nd.year(), nd.month() + 1);
                            const edd = sdd + dim - 1;

                            let nn = 1;
                            const minNights = parseInt($("#MinNights").val() as string, 10);
                            if (nn !== minNights) {
                                nn = minNights;
                            }
                            const ri = $('#SearchBarForm_RoomInfoString').val();
                            const h = $('#Hotel_ID').val();
                            const rc = $('#SearchBarForm_RateCodeSlug').val();

                            const flexiUrl = `/Hotel/GetCalendarAvailability?hotelId=${h}&startDateCounter=${sdd}&endDateCounter=${edd}&numberOfNights=${nn}&roomInfoString=${ri}&rateCodeSlug=${rc}`;

                            $.ajax({
                                type: 'GET',
                                url: flexiUrl,
                                success: (data: any[]) => {
                                    for (let j = 0; j < data.length; j++) {
                                        //_addCalendarPrice(data[j].item1, data[j].item2);
                                        _addCalendarPriceWithMinLos(data[j].date, data[j].price, data[j].minLengthOfStay);
                                    }
                                    if (!monthsRequested.includes(monthRequested)) {
                                        monthsRequested.push(monthRequested);
                                    }
                                    picker.renderAll();
                                },
                                error: () => { },
                            });
                        }
                    }
                }
            });

            picker.on('select', (e: any) => {
                const startDate = picker.getStartDate();
                const endDate = picker.getEndDate();

                // Only proceed if both start and end dates are selected
                if (startDate && endDate) {
                    if (_checkMinMaxNights(startDate, endDate, picker) && !_isRangeContainsCheckOutOnly(startDate, endDate, picker, "hotelInfoRoomResults")) {
                        _setEasePickerDate(picker);

                        const submitButton = document.querySelector('#btnSubmitPackageDealChangeSearch') as HTMLButtonElement | null;
                        if (!submitButton) return;

                        submitButton.click();

                        calendarWidgetContainer.innerHTML = "";
                    }
                }
            });

            picker.on('render', () => {
                // Get the first visible day
                const pickerEl = document.querySelector('.easepick-wrapper');
                if (!pickerEl) return;

                // Access its shadow root
                const shadowRoot = pickerEl.shadowRoot;
                if (!shadowRoot) return;

                const prevBtn = shadowRoot.querySelector('.previous-button');
                const nextBtn = shadowRoot.querySelector('.next-button');

                if (prevBtn) prevBtn.setAttribute('aria-label', 'Previous month');
                if (nextBtn) nextBtn.setAttribute('aria-label', 'Next month');
            });

            picker.on('hide', () => {
                document.querySelectorAll('.tooltip').forEach((el) => (el as HTMLElement).style.display = 'none');
            });

            picker.on('view', (e: any) => {
                calendarWidgetContainer.innerHTML = "";

                const h = $('#Hotel_ID').val();
                if (h !== undefined) {
                    const d = e.detail.date ? e.detail.date.format('YYYY-MM-DD') : null;
                    if (!d) return;

                    if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay') {
                        const target = e.detail.target as HTMLElement;
                        if (!target) return;

                        // Remove any existing content inside the day cell
                        target.innerHTML = '';

                        // Create (or reuse) the span for price placeholder
                        const span =
                            (target.querySelector('.day-price') as HTMLSpanElement | null) ??
                            document.createElement('span');

                        span.className = 'day-price';

                        // Append the span and apply loading class
                        target.append(span);
                        target.classList.add('loading');
                    }

                    if (pricesWithLos[d] !== undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const span = e.detail.target.querySelector('.day-price') || document.createElement('span');
                        if (pricesWithLos[d].price == 'X') {
                            
                            const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                            if (pricesWithLos[lastDate].price !== 'X') {
                                e.detail.target.classList.add('check-out-only');
                                //e.detail.target.setAttribute('title', 'Check out date only');
                                $(e.detail.target).tooltip({ placement: 'bottom' });
                            }
                            else
                            {
                                e.detail.target.classList.add('not-available');
                            }
                        }
                        else {
                            span.className = 'day-price';
                            span.innerHTML = pricesWithLos[d].price;
                            e.detail.target.append(span);
                        }

                    } else if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                        if (pricesWithLos[lastDate] !== undefined) {
                            e.detail.target.classList.add('check-out-only');
                            //e.detail.target.setAttribute('title', 'Check out date only');
                            $(e.detail.target).tooltip({ placement: 'bottom' });
                        } else {
                            e.detail.target.classList.add('locked');
                        }
                    }
                }
            });
        },
    });

    // Set default dates if passed through model
    if (selectedStartDate && selectedEndDate) {
        // 1. Select the range
        picker.setStartDate(selectedStartDate.format('DD-MM-YYYY'));
        picker.setEndDate(selectedEndDate.format('DD-MM-YYYY'));

        // 2. Scroll calendar to start month
        const firstDayOfMonth = new Date(
            selectedStartDate.year(),
            selectedStartDate.month(),
            1
        );
        picker.gotoDate(firstDayOfMonth);

        // 3. Update inputs and headers
        // Only set the date if the user has selected dates
        const selectDatesEl = document.querySelector('.db-no-selected-dates');
        if (!selectDatesEl) _setEasePickerDate(picker);
    }

    picker.clear();

    // ---------------------------
    // Responsive calendar control
    // ---------------------------
    const breakpoint = 768; // Adjust to your "phone-lg" width
    let resizeTimeout: number | undefined;

    function updateCalendarLayout() {
        const newCount = window.innerWidth < breakpoint ? 1 : 2;
        if (picker.options.calendars !== newCount) {
            picker.options.calendars = newCount;
            picker.renderAll();
        }
    }

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(updateCalendarLayout, 150);
    });

    updateCalendarLayout(); // run once on init
}

export function initTransferCalendar(inlineCalendar: boolean) {
    const calendarWidgetContainer = document.getElementById('calendar-widget-container');
    if (!calendarWidgetContainer) return;

    // Prepare initial dates
    let selectedStartDate: moment.Moment | undefined;
    let selectedEndDate: moment.Moment | undefined;

    const arrivalDateInput = (document.getElementById('SearchBarForm_ArrivalDate') as HTMLInputElement | null)?.value;
    const numberOfNightsInput = (document.getElementById('SearchBarForm_NumberOfNights') as HTMLInputElement | null)?.value;
    if (
        arrivalDateInput &&
        arrivalDateInput !== '' &&
        numberOfNightsInput &&
        numberOfNightsInput !== '' &&
        !isNaN(Number(numberOfNightsInput))
    ) {
        selectedStartDate = moment(arrivalDateInput, 'DD-MM-YYYY');
        selectedEndDate = moment(arrivalDateInput, 'DD-MM-YYYY').add(Number(numberOfNightsInput), 'days');
    }

    const startDate = selectedStartDate ? new DateTime(selectedStartDate.toDate()) : undefined;
    const endDate = selectedEndDate ? new DateTime(selectedEndDate.toDate()) : undefined;
    const tenant = Global.getCurrentTenant();

    const picker = new (easepick.create)({
        element: calendarWidgetContainer!,
        css: [
            'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css',
            `${window.location.origin}/css/${tenant}/calendar.css`
        ],
        zIndex: 1000,
        grid: 2,
        format: 'DD-MM-YYYY',
        lang: window.culture,
        inline: true,
        calendars: 2,
        plugins: [RangePlugin, LockPlugin],
        RangePlugin: {
            repick: true,
            startDate: startDate,
            endDate: endDate,
            tooltipNumber: (num: number) => num - 1,
            locale: {
                one: window._screenMessages.Night,
                other: window._screenMessages.Nights,
            },
        },
        LockPlugin: {
            inseparable: true,
            minDate: _getEarliestBookableDate(),
            minDays: 1,
            maxDays: 7
        },
        setup: (picker: any) => {
            // Get the first visible day
            // Get the Easepick wrapper that’s the *next sibling*
            const pickerEl = calendarWidgetContainer.nextElementSibling?.classList.contains('easepick-wrapper')
                ? calendarWidgetContainer.nextElementSibling as HTMLElement
                : null;

            if (!pickerEl) return;

            // Access its shadow root
            const shadowRoot = pickerEl.shadowRoot;
            if (!shadowRoot) return;

            const firstDayEl = shadowRoot.querySelector('.day.unit');
            if (!firstDayEl) return;


            const firstTimestamp = parseInt(firstDayEl.getAttribute('data-time')!, 10);
            let firstDate = moment(firstTimestamp);

            if (selectedStartDate) firstDate = selectedStartDate;

            // Get all visible months in the calendar (2 calendars side by side)
            const visibleMonths: moment.Moment[] = [];

            // Assuming 2 months are visible and get the next one for good measure
            for (let i = 0; i <= 2; i++) {
                const nd = firstDate.clone().startOf('month').add(i, 'month');
                visibleMonths.push(nd);
            }

            // Now loop through them and request prices
            //for (const nd of visibleMonths) {
            //    const monthRequested = `${nd.year()}-${nd.month() + 1}`;
            //    if (!monthsRequested.includes(monthRequested)) {
            //        _loadCalendarPrices(nd, monthRequested, picker);
            //    }
            //}
            _loadCalendarPricesForRange(visibleMonths, picker);

            picker.on('click', (e: MouseEvent) => {
                const target = e.target as HTMLElement | null;
                if (!target) return;

                const element = target.closest('.unit');
                const monthAdd = target.classList.contains('next-button');
                const monthRemove = target.classList.contains('previous-button');
                const closeOutOnlyDate = target.classList.contains('check-out-only');

                const d = picker.calendars[0] ?? null;
                if (d && (monthAdd || monthRemove)) {
                    for (let i = 1; i <= picker.options.calendars; i++) {
                        const nd = moment(new Date(d));
                        if (monthAdd) nd.add(i, 'month');
                        else if (monthRemove) nd.add(-i, 'month');

                        const monthRequested = `${nd.year()}-${nd.month() + 1}`;

                        if (!monthsRequested.includes(monthRequested)) {
                            _loadCalendarPrices(nd, monthRequested, picker);
                        }
                    }
                }
            });

            picker.on('select', (e: any) => {
                const startDate = picker.getStartDate();
                const endDate = picker.getEndDate();

                // Only proceed if both start and end dates are selected
                if (startDate && endDate) {
                    if (_checkMinMaxNights(startDate, endDate, picker) && !_isRangeContainsCheckOutOnly(startDate, endDate, picker, "hotelCalendarSheetContent")) {
                        _setEasePickerDate(picker);

                        SheetHelper.closeAllSheets();
                    }
                }
            });

            picker.on('render', () => {
                // Get the first visible day
                const pickerEl = document.querySelector('.easepick-wrapper');
                if (!pickerEl) return;

                // Access its shadow root
                const shadowRoot = pickerEl.shadowRoot;
                if (!shadowRoot) return;

                const prevBtn = shadowRoot.querySelector('.previous-button');
                const nextBtn = shadowRoot.querySelector('.next-button');

                if (prevBtn) prevBtn.setAttribute('aria-label', 'Previous month');
                if (nextBtn) nextBtn.setAttribute('aria-label', 'Next month');
            });

            picker.on('hide', () => {
                document.querySelectorAll('.tooltip').forEach((el) => (el as HTMLElement).style.display = 'none');
            });

            picker.on('show', () => {
                // Custom validation can go here
            });

            picker.on('view', (e: any) => {
                const h = $('#Hotel_ID').val();

                if (h !== undefined) {
                    const d = e.detail.date ? e.detail.date.format('YYYY-MM-DD') : null;
                    if (!d) return;

                    if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay') {
                        const target = e.detail.target as HTMLElement;
                        if (!target) return;

                        // Remove any existing content inside the day cell
                        target.innerHTML = '';

                        // Create (or reuse) the span for price placeholder
                        const span =
                            (target.querySelector('.day-price') as HTMLSpanElement | null) ??
                            document.createElement('span');

                        span.className = 'day-price';

                        // Append the span and apply loading class
                        target.append(span);
                        target.classList.add('loading');
                    }

                    if (pricesWithLos[d] !== undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const span = e.detail.target.querySelector('.day-price') || document.createElement('span');
                        if (pricesWithLos[d].price == 'X') {
                            const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                            if (pricesWithLos[lastDate].price !== 'X') {
                                e.detail.target.classList.add('check-out-only');
                                //e.detail.target.setAttribute('title', 'Check out date only');
                                $(e.detail.target).tooltip({ placement: 'bottom' });
                            }
                            else {
                                e.detail.target.classList.add('not-available');
                            }
                        }
                        else {
                            span.className = 'day-price';
                            span.innerHTML = pricesWithLos[d].price;
                            e.detail.target.append(span);
                        }

                    } else if (pricesWithLos[d] === undefined && e.detail.view === 'CalendarDay' && !e.detail.target.classList.contains('not-available')) {
                        const lastDate = moment(new Date(d)).add(-1, 'days').format('YYYY-MM-DD');
                        if (pricesWithLos[lastDate] !== undefined) {
                            e.detail.target.classList.add('check-out-only');
                            //e.detail.target.setAttribute('title', 'Check out date only');
                            $(e.detail.target).tooltip({ placement: 'bottom' });
                        } else {
                            e.detail.target.classList.add('locked');
                        }
                    }
                }
            });
        },
    });

    // Set default dates if passed through model
    if (selectedStartDate && selectedEndDate) {
        // 1. Select the range
        picker.setStartDate(selectedStartDate.format('DD-MM-YYYY'));
        picker.setEndDate(selectedEndDate.format('DD-MM-YYYY'));

        // 2. Scroll calendar to start month
        const firstDayOfMonth = new Date(
            selectedStartDate.year(),
            selectedStartDate.month(),
            1
        );
        picker.gotoDate(firstDayOfMonth);

        // 3. Update inputs and headers
        // Only set the date if the user has selected dates
        const selectDatesEl = document.querySelector('.db-no-selected-dates');
        if (!selectDatesEl) _setEasePickerDate(picker);
    }

    if (calendarWidgetContainer) picker.clear();

    // ---------------------------
    // Responsive calendar control
    // ---------------------------
    const breakpoint = 768; // Adjust to your "phone-lg" width
    let resizeTimeout: number | undefined;

    function updateCalendarLayout() {
        const newCount = window.innerWidth < breakpoint ? 1 : 2;
        if (picker.options.calendars !== newCount) {
            picker.options.calendars = newCount;
            picker.renderAll();
        }
    }

    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(updateCalendarLayout, 150);
    });

    updateCalendarLayout(); // run once on init
}